/* eslint-disable react/jsx-props-no-spreading */
import React, { Suspense } from 'react';
import { cn } from 'utils/classNamesTailwind';

/**
 * Builds a ready-to-use React component for your Figma SVGs (Icons or Illustrations).
 * It automatically handles lazy-loading under the hood, so your app only downloads
 * the SVGs it actually needs on the screen.
 *
 * @example
 * // 1. Create the component:
 * export const Icon = createFigmaComponent(ICON_REGISTRY, iconVariants, 'Icon');
 * * // 2. Use it anywhere in your app:
 * // <Icon name="Shopping_Cart" size="md" className="text-gray-500" />
 *
 * @template TName - Powers the autocomplete for your `name` prop.
 * @template TVariants - Powers the autocomplete for your CVA props (like `size`).
 *
 * @param registry - The generated object that links SVG names to their file paths.
 * @param variantsResolver - Your `cva()` function that applies Tailwind classes based on props.
 * @param displayName - The name that will show up in React DevTools (e.g., "Icon").
 *
 * @returns A fully typed React component that accepts your exact SVG names, CVA variants, and standard SVG props.
 */
export function createFigmaComponent<TName extends string, TVariants extends { size?: unknown }>(
  registry: Record<TName, () => Promise<{ default: React.ComponentType<React.SVGProps<SVGSVGElement>> }>>,
  variantsResolver: (props: TVariants) => string,
  displayName: string
) {
  const LazyComponents = Object.fromEntries(
    (Object.keys(registry) as TName[]).map(key => {
      const importFn = registry[key];
      const LazyComp = React.lazy(async () => {
        try {
          return await importFn();
        } catch (err) {
          console.error(`Failed to load ${displayName.toLowerCase()} "${key}":`, err);
          return { default: () => null };
        }
      });
      return [key, LazyComp];
    })
  ) as Record<TName, React.LazyExoticComponent<React.ComponentType<React.SVGProps<SVGSVGElement>>>>;

  type ComponentProps = Omit<React.SVGProps<SVGSVGElement>, 'name'> &
    TVariants & {
      name: TName;
      className?: string;
      'aria-label'?: string;
    };

  const Component = React.forwardRef<SVGSVGElement, ComponentProps>(
    ({ name, size, className, 'aria-label': ariaLabel, ...props }, ref) => {
      const LazyAsset = LazyComponents[name] as React.ElementType<React.SVGProps<SVGSVGElement>> | undefined;

      if (!LazyAsset) {
        console.warn(`${displayName} "${name}" not found in registry`);
        return null;
      }

      const variantClasses = cn(variantsResolver({ size } as TVariants), className);

      return (
        <Suspense fallback={null}>
          <LazyAsset ref={ref} className={variantClasses} aria-label={ariaLabel || name} {...props} />
        </Suspense>
      );
    }
  );

  Component.displayName = displayName;

  return Component;
}
